import { useState, useCallback, useRef } from 'react';
import { ShowMetaDataDTO, ShowMetaDataType } from '../types/Show';
import { toast } from 'sonner';

// Debounce utility function
function debounce(func: (...args: any[]) => void, wait: number) {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

interface UseShowMetadataProps {
  initialMetadata?: ShowMetaDataDTO[];
  onMetadataChange?: (metadata: ShowMetaDataDTO[]) => void;
  onMetadataAutoSave?: (metadata: ShowMetaDataDTO[]) => Promise<void>;
  autoSaveDelay?: number;
  showId?: string;
}

export const useShowMetadata = ({ 
  initialMetadata = [], 
  onMetadataChange,
  onMetadataAutoSave,
  autoSaveDelay = 1000,
  showId
}: UseShowMetadataProps = {}) => {
  const [metadata, setMetadata] = useState<ShowMetaDataDTO[]>(initialMetadata);
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  // Debounced auto-save function
  const debouncedAutoSave = useCallback(
    debounce(async (newMetadata: ShowMetaDataDTO[]) => {
      if (!onMetadataAutoSave || !showId || isAutoSaving) return;

      try {
        setIsAutoSaving(true);
        await onMetadataAutoSave(newMetadata);
        toast.success('Metadata saved automatically', { duration: 2000 });
      } catch (error) {
        console.error('Auto-save metadata error:', error);
        toast.error('Failed to save metadata changes');
      } finally {
        setIsAutoSaving(false);
      }
    }, autoSaveDelay),
    [onMetadataAutoSave, showId, isAutoSaving, autoSaveDelay]
  );

  const updateMetadata = useCallback((type: ShowMetaDataType, value: string, silent: boolean = false) => {
    setMetadata(prev => {
      const existingIndex = prev.findIndex(item => item.type === type);
      let newMetadata: ShowMetaDataDTO[];
      
      if (existingIndex >= 0) {
        // Update existing metadata
        newMetadata = [...prev];
        newMetadata[existingIndex] = { ...newMetadata[existingIndex], value };
      } else {
        // Add new metadata
        newMetadata = [...prev, { type, value }];
      }
      
      // Call onChange callback
      onMetadataChange?.(newMetadata);
      
      // Trigger auto-save if enabled and not silent
      if (!silent && onMetadataAutoSave && showId) {
        debouncedAutoSave(newMetadata);
      }
      
      return newMetadata;
    });
  }, [onMetadataChange, onMetadataAutoSave, showId, debouncedAutoSave]);

  const getMetadataValue = useCallback((type: ShowMetaDataType): string => {
    return metadata.find(item => item.type === type)?.value || '';
  }, [metadata]);

  const removeMetadata = useCallback((type: ShowMetaDataType) => {
    setMetadata(prev => {
      const newMetadata = prev.filter(item => item.type !== type);
      onMetadataChange?.(newMetadata);
      return newMetadata;
    });
  }, [onMetadataChange]);

  const resetMetadata = useCallback(() => {
    setMetadata([]);
    onMetadataChange?.([]);
  }, [onMetadataChange]);

  const getThumbnailMetadata = useCallback(() => {
    return metadata.filter(item => 
      item.type === ShowMetaDataType.BANNER_THUMBNAIL_V2 ||
      item.type === ShowMetaDataType.WIDGET_THUMBNAIL_V2 ||
      item.type === ShowMetaDataType.DETAIL_PAGE_THUMBNAIL_V2 ||
      item.type === ShowMetaDataType.CONTINUE_READING_THUMBNAIL_V2
    );
  }, [metadata]);

  const setDefaultMetadata = useCallback(() => {
    const defaultMetadata: ShowMetaDataDTO[] = [
      { type: ShowMetaDataType.BANNER_THUMBNAIL_V2, value: '' },
      { type: ShowMetaDataType.WIDGET_THUMBNAIL_V2, value: '' },
      { type: ShowMetaDataType.DETAIL_PAGE_THUMBNAIL_V2, value: '' },
      { type: ShowMetaDataType.CONTINUE_READING_THUMBNAIL_V2, value: '' },
      { type: ShowMetaDataType.COMIC_STYLE, value: 'default' },
      { type: ShowMetaDataType.STYLE_NAME, value: 'Default Style' },
    ];
    
    setMetadata(defaultMetadata);
    onMetadataChange?.(defaultMetadata);
  }, [onMetadataChange]);

  // Manual save function for immediate updates
  const saveMetadataImmediately = useCallback(async (metadataToSave?: ShowMetaDataDTO[]) => {
    if (!onMetadataAutoSave || !showId) return;

    const dataToSave = metadataToSave || metadata;
    
    try {
      setIsAutoSaving(true);
      await onMetadataAutoSave(dataToSave);
      toast.success('Metadata saved successfully');
    } catch (error) {
      console.error('Manual save metadata error:', error);
      toast.error('Failed to save metadata');
      throw error;
    } finally {
      setIsAutoSaving(false);
    }
  }, [onMetadataAutoSave, showId, metadata]);

  return {
    metadata,
    setMetadata,
    updateMetadata,
    getMetadataValue,
    removeMetadata,
    resetMetadata,
    getThumbnailMetadata,
    setDefaultMetadata,
    saveMetadataImmediately,
    isAutoSaving,
  };
};